/**
 * Task CRUD, expressed in Task terms.
 *
 * The rule that matters: a task is identified by its TaskID, never by its row.
 * Rows are re-resolved from the sheet immediately before every write, so
 * sorting the sheet by hand in Google Sheets cannot land an edit on the wrong
 * task. Nothing here caches a row number across an await.
 */
import { config } from "../config";
import { sheets } from "../lib/sheets";
import {
  TASKS_FIRST_DATA_ROW,
  TASKS_READ_FIRST,
  TASKS_READ_LAST,
  columnLetter,
  columnNumber,
  hasExplicitStatus,
  isBlankRow,
  rowToTask,
  taskCell,
  taskUpdates,
  tasksReadRange,
} from "../lib/schema";
import type { Task, TaskDraft } from "../types";

const tab = () => config.tasksTab;

export function newTaskId(): string {
  return crypto.randomUUID();
}

export interface RawRow {
  row: unknown[];
  sheetRow: number;
}

/** Every populated row, blank spacers excluded, soft-deleted included. */
export async function readRows(token: string): Promise<RawRow[]> {
  const rows = await sheets.read(tasksReadRange(tab()), token);
  return rows
    .map((row, index) => ({ row, sheetRow: index + TASKS_FIRST_DATA_ROW }))
    .filter(({ row }) => !isBlankRow(row));
}

export async function listAll(token: string): Promise<Task[]> {
  return (await readRows(token)).map(({ row, sheetRow }) => rowToTask(row, sheetRow));
}

/** The live task list: everything the board may show. */
export async function list(token: string): Promise<Task[]> {
  return (await listAll(token)).filter((t) => !t.deleted);
}

/** Rows that carry a task but no valid status, which setup fixes. */
export async function rowsWithoutStatus(token: string): Promise<number[]> {
  return (await readRows(token))
    .filter(({ row }) => !hasExplicitStatus(row))
    .map(({ sheetRow }) => sheetRow);
}

async function resolveRow(id: string, token: string): Promise<number> {
  if (!id) throw new Error("That task has no TaskID yet. Run setup first.");
  const match = (await listAll(token)).find((t) => t.id === id);
  if (!match) {
    throw new Error("That task is no longer in the sheet. Refresh to reload.");
  }
  return match.sheetRow;
}

export async function create(draft: TaskDraft, token: string): Promise<Task> {
  const task: Task = {
    ...draft,
    id: newTaskId(),
    calendarEventId: "",
    deleted: false,
    sheetRow: -1,
  };

  // Append writes one contiguous block, so the new row spans B:Q including the
  // Due days column. A new row has no formula there to preserve, and leaving
  // the cell empty is correct: the sheet's own fill-down owns it.
  const blank = "";
  const width = columnNumber(TASKS_READ_LAST) - columnNumber(TASKS_READ_FIRST) + 1;
  const values: (string | number)[] = new Array(width).fill(blank);
  for (const update of taskUpdates(tab(), task, 0)) {
    const start = update.range.split("!")[1]!.match(/^([A-Z]+)/)![1]!;
    const offset = columnNumber(start) - columnNumber(TASKS_READ_FIRST);
    update.values.forEach((v, i) => {
      values[offset + i] = v;
    });
  }

  const sheetRow = await sheets.append(tasksReadRange(tab()), values, token);
  return { ...task, sheetRow };
}

export async function update(task: Task, token: string): Promise<Task> {
  const sheetRow = await resolveRow(task.id, token);
  await sheets.batchWrite(taskUpdates(tab(), task, sheetRow), token);
  return { ...task, sheetRow };
}

/**
 * Write calendar event ids into column K.
 *
 * Rows are resolved from a single fresh read rather than from the caller's
 * cached tasks, keeping the identity rule intact without one lookup per task.
 */
export async function setCalendarEventIds(
  changes: Map<string, string>,
  token: string
): Promise<void> {
  if (!changes.size) return;
  const rowById = new Map((await listAll(token)).map((t) => [t.id, t.sheetRow]));
  const writes = [];
  for (const [taskId, eventId] of changes) {
    const row = rowById.get(taskId);
    if (row === undefined) continue;
    writes.push({
      range: taskCell(tab(), "calendarEventId", row),
      values: [eventId],
    });
  }
  for (let i = 0; i < writes.length; i += 200) {
    await sheets.batchWrite(writes.slice(i, i + 200), token);
  }
}

/** Soft delete. Nothing is ever removed from the sheet. */
export async function remove(task: Task, token: string): Promise<void> {
  const sheetRow = await resolveRow(task.id, token);
  await sheets.batchWrite(
    [{ range: taskCell(tab(), "deleted", sheetRow), values: ["DELETED"] }],
    token
  );
}

export { columnLetter };
